import React, { createContext, useContext, useState, useEffect } from "react";
import { useAuth } from "./AuthContext";
import { useRepairShopContext } from "./RepairShopContext";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Invoice,
  InvoiceFormData,
  Client,
  ClientFormData,
} from "@/types/stock";
import {
  EnhancedInvoice,
  PaymentRecord,
  PaymentRecordFormData,
  PaymentSummary,
  PaymentAnalytics,
} from "@/types/invoice";
import { PaymentService } from "@/services/PaymentService";

interface InvoiceContextType {
  // Invoices
  invoices: Invoice[];
  enhancedInvoices: EnhancedInvoice[];
  clients: Client[];
  loading: boolean;

  // Invoice operations
  createInvoice: (data: InvoiceFormData) => Promise<Invoice>;
  updateInvoice: (id: string, data: Partial<InvoiceFormData>) => Promise<void>;
  deleteInvoice: (id: string) => Promise<void>;
  getInvoiceById: (id: string) => Promise<Invoice | undefined>;
  getEnhancedInvoiceById: (id: string) => Promise<EnhancedInvoice | undefined>;

  // Client operations
  createClient: (data: ClientFormData) => Promise<Client>;
  updateClient: (id: string, data: Partial<ClientFormData>) => Promise<void>;
  deleteClient: (id: string) => Promise<void>;

  // Payment operations
  createPaymentRecord: (data: PaymentRecordFormData) => Promise<PaymentRecord>;
  updatePaymentRecord: (
    id: string,
    data: Partial<PaymentRecordFormData>
  ) => Promise<void>;
  deletePaymentRecord: (id: string) => Promise<void>;
  getPaymentRecordsForInvoice: (invoiceId: string) => Promise<PaymentRecord[]>;
  getPaymentSummary: (invoiceId: string) => Promise<PaymentSummary>;

  // Analytics and reporting
  getPaymentAnalytics: (
    startDate?: Date,
    endDate?: Date
  ) => Promise<PaymentAnalytics>;
  getOverdueInvoices: () => Promise<EnhancedInvoice[]>;
  updateOverdueInvoices: () => Promise<number>;

  // Refresh functions
  refreshInvoices: () => Promise<void>;
  refreshClients: () => Promise<void>;
  refreshPaymentData: (invoiceId?: string) => Promise<void>;
}

const InvoiceContext = createContext<InvoiceContextType | undefined>(undefined);

export const useInvoices = () => {
  const context = useContext(InvoiceContext);
  if (!context) {
    throw new Error("useInvoices must be used within an InvoiceProvider");
  }
  return context;
};

interface InvoiceProviderProps {
  children: React.ReactNode;
}

export const InvoiceProvider: React.FC<InvoiceProviderProps> = ({
  children,
}) => {
  const { user } = useAuth();
  const { repairShop } = useRepairShopContext();
  const { t } = useTranslation();

  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [enhancedInvoices, setEnhancedInvoices] = useState<EnhancedInvoice[]>(
    []
  );
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user && repairShop) {
      refreshAll();
      setupRealtimeSubscriptions();
    } else {
      setInvoices([]);
      setClients([]);
      setLoading(false);
    }
  }, [user, repairShop]);

  const refreshAll = async () => {
    try {
      await Promise.all([refreshClients(), refreshInvoices()]);
    } catch (error) {
      console.error("Error refreshing invoice data:", error);
    }
    setLoading(false);
  };

  const setupRealtimeSubscriptions = () => {
    if (!repairShop) return;

    // Invoices subscription
    const invoicesSubscription = supabase
      .channel("invoices-changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "invoices",
          filter: `repair_shop_id=eq.${repairShop.id}`,
        },
        () => refreshInvoices()
      )
      .subscribe();

    // Clients subscription
    const clientsSubscription = supabase
      .channel("clients-changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "clients",
          filter: `repair_shop_id=eq.${repairShop.id}`,
        },
        () => refreshClients()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(invoicesSubscription);
      supabase.removeChannel(clientsSubscription);
    };
  };

  const refreshClients = async () => {
    if (!user || !repairShop) return;

    try {
      const { data, error } = await supabase
        .from("clients")
        .select("*")
        .eq("repair_shop_id", repairShop.id)
        .order("name");

      if (error) throw error;

      const processedClients: Client[] = data.map((client) => ({
        id: client.id,
        name: client.name,
        phone: client.phone || undefined,
        email: client.email || undefined,
        address: client.address || undefined,
        notes: client.notes || undefined,
        creditLimit: client.credit_limit,
        currentCreditBalance: client.current_credit_balance,
        isActive: client.is_active,
        repairShopId: client.repair_shop_id,
        createdAt: new Date(client.created_at),
        updatedAt: new Date(client.updated_at),
      }));

      setClients(processedClients);
    } catch (error) {
      console.error("Error fetching clients:", error);
      toast.error(t("invoices.fetchError"));
    }
  };

  const refreshInvoices = async () => {
    if (!user || !repairShop) return;

    try {
      const { data, error } = await supabase
        .from("invoices")
        .select(
          `
          *,
          client:clients(*),
          items:invoice_items(*)
        `
        )
        .eq("repair_shop_id", repairShop.id)
        .order("created_at", { ascending: false });

      if (error) throw error;

      const processedInvoices: Invoice[] = data.map((invoice) => ({
        id: invoice.id,
        invoiceNumber: invoice.invoice_number,
        clientId: invoice.client_id || undefined,
        client: invoice.client
          ? {
              id: invoice.client.id,
              name: invoice.client.name,
              phone: invoice.client.phone || undefined,
              email: invoice.client.email || undefined,
              address: invoice.client.address || undefined,
              notes: invoice.client.notes || undefined,
              creditLimit: invoice.client.credit_limit,
              currentCreditBalance: invoice.client.current_credit_balance,
              isActive: invoice.client.is_active,
              repairShopId: invoice.client.repair_shop_id,
              createdAt: new Date(invoice.client.created_at),
              updatedAt: new Date(invoice.client.updated_at),
            }
          : undefined,
        clientName: invoice.client_name,
        clientPhone: invoice.client_phone || undefined,
        clientEmail: invoice.client_email || undefined,
        clientAddress: invoice.client_address || undefined,
        subtotal: invoice.subtotal,
        taxRate: invoice.tax_rate,
        taxAmount: invoice.tax_amount,
        discountAmount: invoice.discount_amount,
        totalAmount: invoice.total_amount,
        status: invoice.status as Invoice["status"],
        dueDate: invoice.due_date ? new Date(invoice.due_date) : undefined,
        notes: invoice.notes || undefined,
        termsAndConditions: invoice.terms_and_conditions || undefined,
        repairShopId: invoice.repair_shop_id,
        createdBy: invoice.created_by,
        createdAt: new Date(invoice.created_at),
        updatedAt: new Date(invoice.updated_at),
        items: invoice.items.map((item: any) => ({
          id: item.id,
          invoiceId: item.invoice_id,
          productId: item.product_id || undefined,
          productName: item.product_name,
          description: item.description || undefined,
          quantity: item.quantity,
          unitPrice: item.unit_price,
          discountPercentage: item.discount_percentage,
          discountAmount: item.discount_amount,
          totalPrice: item.total_price,
          createdAt: new Date(item.created_at),
          updatedAt: new Date(item.updated_at),
        })),
      }));

      setInvoices(processedInvoices);
    } catch (error) {
      console.error("Error fetching invoices:", error);
      toast.error(t("invoices.fetchError"));
    }
  };

  // Client CRUD operations
  const createClient = async (data: ClientFormData): Promise<Client> => {
    if (!user || !repairShop) throw new Error("User or repair shop not found");

    try {
      const { data: insertedData, error } = await supabase
        .from("clients")
        .insert({
          name: data.name,
          phone: data.phone,
          email: data.email,
          address: data.address,
          notes: data.notes,
          credit_limit: data.creditLimit,
          is_active: data.isActive,
          repair_shop_id: repairShop.id,
        })
        .select()
        .single();

      if (error) throw error;

      const newClient: Client = {
        id: insertedData.id,
        name: insertedData.name,
        phone: insertedData.phone || undefined,
        email: insertedData.email || undefined,
        address: insertedData.address || undefined,
        notes: insertedData.notes || undefined,
        creditLimit: insertedData.credit_limit,
        currentCreditBalance: insertedData.current_credit_balance,
        isActive: insertedData.is_active,
        repairShopId: insertedData.repair_shop_id,
        createdAt: new Date(insertedData.created_at),
        updatedAt: new Date(insertedData.updated_at),
      };

      setClients([...clients, newClient]);
      return newClient;
    } catch (error) {
      console.error("Error creating client:", error);
      throw error;
    }
  };

  const updateClient = async (
    id: string,
    data: Partial<ClientFormData>
  ): Promise<void> => {
    try {
      const { error } = await supabase
        .from("clients")
        .update({
          name: data.name,
          phone: data.phone,
          email: data.email,
          address: data.address,
          notes: data.notes,
          credit_limit: data.creditLimit,
          is_active: data.isActive,
          updated_at: new Date().toISOString(),
        })
        .eq("id", id);

      if (error) throw error;

      await refreshClients();
    } catch (error) {
      console.error("Error updating client:", error);
      throw error;
    }
  };

  const deleteClient = async (id: string): Promise<void> => {
    try {
      const { error } = await supabase.from("clients").delete().eq("id", id);

      if (error) throw error;

      setClients(clients.filter((c) => c.id !== id));
    } catch (error) {
      console.error("Error deleting client:", error);
      throw error;
    }
  };

  // Invoice CRUD operations
  const createInvoice = async (data: InvoiceFormData): Promise<Invoice> => {
    if (!user || !repairShop) throw new Error("User or repair shop not found");

    try {
      // Generate invoice number
      const { data: invoiceNumberData } = await supabase.rpc(
        "generate_invoice_number",
        { shop_id: repairShop.id }
      );

      const invoiceNumber = invoiceNumberData || `INV-${Date.now()}`;

      // Calculate totals
      const subtotal = data.items.reduce((sum, item) => {
        const itemTotal = item.quantity * item.unitPrice;
        const discount = (itemTotal * item.discountPercentage) / 100;
        return sum + (itemTotal - discount);
      }, 0);

      const taxAmount = (subtotal * data.taxRate) / 100;
      const totalAmount = subtotal + taxAmount - data.discountAmount;

      const { data: insertedData, error } = await supabase
        .from("invoices")
        .insert({
          invoice_number: invoiceNumber,
          client_id: data.clientId || null, // Convert empty string to null
          client_name: data.clientName,
          client_phone: data.clientPhone,
          client_email: data.clientEmail,
          client_address: data.clientAddress,
          subtotal,
          tax_rate: data.taxRate,
          tax_amount: taxAmount,
          discount_amount: data.discountAmount,
          total_amount: totalAmount,
          due_date: data.dueDate?.toISOString(),
          notes: data.notes,
          terms_and_conditions: data.termsAndConditions,
          repair_shop_id: repairShop.id,
          created_by: user.id,
        })
        .select()
        .single();

      if (error) throw error;

      // Insert invoice items
      if (data.items.length > 0) {
        const itemsToInsert = data.items.map((item) => ({
          invoice_id: insertedData.id,
          product_id: item.productId || null, // Convert empty string to null
          product_name: item.productName,
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unitPrice,
          discount_percentage: item.discountPercentage,
          discount_amount:
            (item.quantity * item.unitPrice * item.discountPercentage) / 100,
          total_price:
            item.quantity * item.unitPrice -
            (item.quantity * item.unitPrice * item.discountPercentage) / 100,
        }));

        const { error: itemsError } = await supabase
          .from("invoice_items")
          .insert(itemsToInsert);

        if (itemsError) throw itemsError;
      }

      await refreshInvoices();
      return insertedData as any; // Will be properly typed after refresh
    } catch (error) {
      console.error("Error creating invoice:", error);
      throw error;
    }
  };

  const updateInvoice = async (
    id: string,
    data: Partial<InvoiceFormData>
  ): Promise<void> => {
    try {
      // Calculate totals if items are provided
      let updateData: any = {
        client_id: data.clientId || null, // Convert empty string to null
        client_name: data.clientName,
        client_phone: data.clientPhone,
        client_email: data.clientEmail,
        client_address: data.clientAddress,
        tax_rate: data.taxRate,
        discount_amount: data.discountAmount,
        due_date: data.dueDate?.toISOString(),
        notes: data.notes,
        terms_and_conditions: data.termsAndConditions,
        updated_at: new Date().toISOString(),
      };

      if (data.items) {
        const subtotal = data.items.reduce((sum, item) => {
          const itemTotal = item.quantity * item.unitPrice;
          const discount = (itemTotal * item.discountPercentage) / 100;
          return sum + (itemTotal - discount);
        }, 0);

        const taxAmount = (subtotal * (data.taxRate || 0)) / 100;
        const totalAmount = subtotal + taxAmount - (data.discountAmount || 0);

        updateData = {
          ...updateData,
          subtotal,
          tax_amount: taxAmount,
          total_amount: totalAmount,
        };
      }

      const { error } = await supabase
        .from("invoices")
        .update(updateData)
        .eq("id", id);

      if (error) throw error;

      // Update invoice items if provided
      if (data.items) {
        // Delete existing items
        await supabase.from("invoice_items").delete().eq("invoice_id", id);

        // Insert new items
        if (data.items.length > 0) {
          const itemsToInsert = data.items.map((item) => ({
            invoice_id: id,
            product_id: item.productId || null, // Convert empty string to null
            product_name: item.productName,
            description: item.description,
            quantity: item.quantity,
            unit_price: item.unitPrice,
            discount_percentage: item.discountPercentage,
            discount_amount:
              (item.quantity * item.unitPrice * item.discountPercentage) / 100,
            total_price:
              item.quantity * item.unitPrice -
              (item.quantity * item.unitPrice * item.discountPercentage) / 100,
          }));

          const { error: itemsError } = await supabase
            .from("invoice_items")
            .insert(itemsToInsert);

          if (itemsError) throw itemsError;
        }
      }

      await refreshInvoices();
    } catch (error) {
      console.error("Error updating invoice:", error);
      throw error;
    }
  };

  const deleteInvoice = async (id: string): Promise<void> => {
    try {
      const { error } = await supabase.from("invoices").delete().eq("id", id);

      if (error) throw error;

      setInvoices(invoices.filter((i) => i.id !== id));
    } catch (error) {
      console.error("Error deleting invoice:", error);
      throw error;
    }
  };

  const getInvoiceById = async (id: string): Promise<Invoice | undefined> => {
    return invoices.find((invoice) => invoice.id === id);
  };

  return (
    <InvoiceContext.Provider
      value={{
        invoices,
        clients,
        loading,
        createInvoice,
        updateInvoice,
        deleteInvoice,
        getInvoiceById,
        createClient,
        updateClient,
        deleteClient,
        refreshInvoices,
        refreshClients,
      }}
    >
      {children}
    </InvoiceContext.Provider>
  );
};

export default InvoiceProvider;
